<script>
    import {
        Chart as ChartJS,
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        Colors,
        Filler,
    } from 'chart.js';
    import { Line } from 'vue-chartjs';

    import ChartDataLabels from 'chartjs-plugin-datalabels';

    ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        Colors,
        Filler,
        ChartDataLabels,
    );

    // Import general mixin for shared functionality.
    import mixin from '../../mixin';

    export default {
        mixins: [mixin],
        components: {
            Line,
        },
        props: {
            data: Object,
            dates: Array,
            isClosed: Boolean,
        },
        data() {
            return {
                facetAlias: this.isClosed ? 'afsluitjaarmaand' : 'ingangsjaarmaand',
                facetAliasHeadsum: 'hoofdsom_totaal',
                facetAliasReceived: 'bedrag_door_u_ontvangen_inc_btw_voordeel',
                facetAliasPaid: 'bedrag_door_u_betaald_inc_btw',
                options: {
                    locale: 'nl-NL',
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom',
                        },
                        title: {
                            display: true,
                            text: this.isClosed ? this.$store.getters.getText('dashboardWidgetEfficiencymonthend') : this.$store.getters.getText('dashboardWidgetEfficiencymonth'),
                            padding: {
                                top: 10,
                                right: 10,
                                bottom: 20,
                                left: 10,
                            }
                        },
                        tooltip: {
                            callbacks: {
                                title: (context) => `${context[0].dataset.label}`,
                                beforeLabel: (context) => `${context.label}`,
                                label: (context) => `${this.$store.getters.getText('dashboardWidgetPercentage')}: ${context.parsed.y}%`,
                            },
                        },
                        datalabels: {
                            display: 'auto',
                            anchor: 'center',
                            align: 'top',
                            clamp: true,
                            font: {
                                lineHeight: 1,
                            },
                            formatter: (value, ctx) => value + '%',
                        },

                    },
                    scales: {
                        y: {
                            ticks: {
                                callback: (value) => value + '%',
                            },
                        },
                        x: {
                            grid: {
                                display: false
                            },
                            ticks: {
                                autoSkip: false,
                                maxRotation: 0,
                                callback: (index) => {
                                    const label = this.dataSets.labels[index];
                                    if (!label || !label.endsWith('-1')) return '';
                                    return label.split('-')[0] % 2 == 0 ? label.split('-')[0] : '';
                                },
                            }
                        }
                    },
                },
            };
        },
        computed: {
            facetsSet() {
                return this.data && this.data.facets && this.data.facets.length;
            },
            datesSet() {
                return this.dates && this.dates.length;
            },
            dataSets() {
                const defaultValue = { labels: [], datasets: [] };
                if (!this.facetsSet || !this.datesSet) return defaultValue;
                const facet = this.data.facets.find(f => f.alias === this.facetAlias);
                if (!facet || !facet.options || !facet.options.length) return defaultValue;

                const labels = this.dates.map(date => date.label);
                const dataset = [];

                this.dates.forEach(date => {
                    const option = facet.options.find(o => o.valueAlias === date.label);
                    const datapoint = option ? this.calculateEfficiency(option) : 0;
                    dataset.push(datapoint);
                });

                const highContrast = this.$store.getters.getHighContrast;
                const borderColor = highContrast ? '#833de6' : '#53b7d8';
                const backgroundColor = highContrast ? '#f1e9fc' : '#edf8fb';
                const pointBackgroundColor = highContrast ? '#833de6' : '#53b7d8';

                return {
                    labels: labels,
                    datasets: [
                        {
                            label: this.isClosed ? this.$store.getters.getText('dashboardWidgetEnddate') : this.$store.getters.getText('dashboardWidgetStartdate'),
                            data: dataset,
                            fill: true,
                            borderColor: borderColor,
                            backgroundColor: backgroundColor,
                            pointBackgroundColor: pointBackgroundColor,
                        },
                    ],
                };
            },
        },
        methods: {
            calculateEfficiency(option) {
                const sumReceived = option.sums[this.facetAliasReceived] || 0;
                const sumPaid = option.sums[this.facetAliasPaid] || 0;
                const headSum = option.sums[this.facetAliasHeadsum] || 0;
                return headSum !== 0 ? Math.round((sumReceived + sumPaid) / headSum * 100) : 0;
            },
        },
    };
</script>

<template>
    <div class="dashboard-widget dashboard-widget--minheight">
        <Line :data="dataSets" :options="options" />
    </div>
</template>
